/**
 * @copyright 2024-2026 David Baugh
 * @license Proprietary - All Rights Reserved
 * Unauthorized copying, modification, or distribution is strictly prohibited.
 */

import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, 
  ReferenceLine, Cell, AreaChart, Area, ScatterChart, Scatter, RadarChart,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';
import { format } from 'date-fns';
import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from "../ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";

// Enhanced color palette for improved visual appeal
const COLORS = {
  primary: '#2563eb',    // blue-600
  secondary: '#f97316',  // orange-500
  success: '#22c55e',    // green-500
  danger: '#ef4444',     // red-500
  warning: '#f59e0b',    // amber-500
  info: '#0ea5e9',       // sky-500
  accent: '#8b5cf6',     // violet-500
  neutralDark: '#1e293b', // slate-800
  neutralLight: '#94a3b8', // slate-400
  paleBlue: '#dbeafe',   // blue-100
  paleGreen: '#dcfce7',  // green-100
  paleRed: '#fee2e2',    // red-100
  chartGradientStart: '#dbeafe',
  chartGradientEnd: '#2563eb',
  pieColors: ['#2563eb', '#f97316', '#22c55e', '#ef4444', '#f59e0b', '#0ea5e9']
};

// Helper functions
const safeParseFloat = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

const formatTimeSpent = (seconds) => {
  if (!seconds) return '0m';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
};

// Main component
const StudentMetrics = ({ studentData, testResults, failedQuestions }) => {
  // Process and prepare data for visualization - MOVED BEFORE ANY CONDITIONALS
  const processedData = useMemo(() => {
    // Handle missing or malformed data
    if (!testResults || testResults.length === 0) {
      return {
        hasData: false,
        chronologicalResults: [],
        bestScoresByTest: [],
        scoreRanges: [],
        flatLearningCurve: [],
        timeVsScoreData: [],
        radarData: [],
        metrics: {
          totalTests: 0,
          totalAttempts: 0,
          passedTests: 0,
          avgAttemptsPerTest: 0,
          avgScore: 0,
          totalTimeSpent: 0,
          avgTimePerTest: 0
        }
      };
    }

    // Format and standardize test result data
    const formattedResults = testResults.map(result => ({
      id: result.result_id || result.id,
      testId: result.test_id,
      testName: result.test_name || 'Unknown Test',
      score: safeParseFloat(result.raw_percentage || result.score),
      timeSpent: result.time_spent ? result.time_spent / 60 : 0, // Convert to minutes
      timestamp: result.attempt_timestamp ? new Date(result.attempt_timestamp) : null,
      passed: result.passed === true
    }));

    // Sort by date for timeline visualization
    const chronologicalResults = [...formattedResults]
      .filter(r => r.timestamp)
      .sort((a, b) => a.timestamp - b.timestamp);
    
    // Group by test for test-specific analysis
    const testGroups = formattedResults.reduce((acc, result) => {
      if (!acc[result.testName]) {
        acc[result.testName] = [];
      }
      acc[result.testName].push(result);
      return acc;
    }, {});

    // Get best scores by test
    const bestScoresByTest = Object.entries(testGroups).map(([testName, attempts]) => {
      // Find best attempt
      const bestAttempt = [...attempts].sort((a, b) => b.score - a.score)[0];
      return {
        testName,
        bestScore: bestAttempt.score,
        attempts: attempts.length,
        averageScore: attempts.reduce((sum, a) => sum + a.score, 0) / attempts.length,
        averageTime: attempts.reduce((sum, a) => sum + a.timeSpent, 0) / attempts.length,
        firstAttemptDate: attempts.sort((a, b) => a.timestamp - b.timestamp)[0]?.timestamp,
        lastAttemptDate: attempts.sort((a, b) => b.timestamp - a.timestamp)[0]?.timestamp,
        improvementTrend: attempts.length > 1 ? 
          attempts.sort((a, b) => a.timestamp - b.timestamp).map(a => a.score) : []
      };
    });

    // Create score distribution data
    const scoreRanges = [
      { range: '0-9', count: 0 },
      { range: '10-19', count: 0 },
      { range: '20-29', count: 0 },
      { range: '30-39', count: 0 },
      { range: '40-49', count: 0 },
      { range: '50-59', count: 0 },
      { range: '60-69', count: 0 },
      { range: '70-79', count: 0 },
      { range: '80-89', count: 0 },
      { range: '90-100', count: 0 }
    ];
    
    formattedResults.forEach(result => {
      const score = result.score;
      if (score < 10) scoreRanges[0].count++;
      else if (score < 20) scoreRanges[1].count++;
      else if (score < 30) scoreRanges[2].count++;
      else if (score < 40) scoreRanges[3].count++;
      else if (score < 50) scoreRanges[4].count++;
      else if (score < 60) scoreRanges[5].count++;
      else if (score < 70) scoreRanges[6].count++;
      else if (score < 80) scoreRanges[7].count++;
      else if (score < 90) scoreRanges[8].count++;
      else scoreRanges[9].count++;
    });

    // Generate learning curve data
    const learningCurveData = Object.entries(testGroups).map(([testName, attempts]) => {
      // Sort attempts chronologically for each test
      const sortedAttempts = [...attempts].sort((a, b) => a.timestamp - b.timestamp);
      
      // Return data points showing progression
      return {
        testName,
        progression: sortedAttempts.map((attempt, index) => ({
          attempt: index + 1,
          score: attempt.score,
          timestamp: attempt.timestamp,
          testName
        }))
      };
    });

    // Flatten learning curve data for the chart
    const flatLearningCurve = learningCurveData
      .flatMap(test => test.progression)
      .sort((a, b) => a.timestamp - b.timestamp);

    // Generate time vs score scatter plot data
    const timeVsScoreData = formattedResults.map(result => ({
      timeSpent: result.timeSpent,
      score: result.score,
      testName: result.testName,
      passed: result.passed
    }));

    // Calculate performance metrics
    const totalTests = bestScoresByTest.length;
    const totalAttempts = formattedResults.length;
    const passedTests = bestScoresByTest.filter(test =>
      // Check if any attempt for this test passed
      formattedResults.some(result => result.testName === test.testName && result.passed === true)
    ).length;
    const avgAttemptsPerTest = totalTests > 0 ? totalAttempts / totalTests : 0;
    const avgScore = totalAttempts > 0 ? formattedResults.reduce((sum, r) => sum + r.score, 0) / totalAttempts : 0;
    const totalTimeSpent = formattedResults.reduce((sum, r) => sum + r.timeSpent, 0);
    const avgTimePerTest = totalAttempts > 0 ? totalTimeSpent / totalAttempts : 0;

    // Generate performance radar data
    const radarData = [
      {
        subject: 'Accuracy',
        score: avgScore / 100 * 10, // Scale to 0-10
        fullMark: 10
      },
      {
        subject: 'Efficiency',
        score: Math.max(10 - avgAttemptsPerTest * 2, 1), // Lower attempts = higher score
        fullMark: 10
      },
      {
        subject: 'Speed',
        score: Math.max(10 - (avgTimePerTest / 10), 1), // Faster = higher score
        fullMark: 10
      },
      {
        subject: 'Consistency',
        score: Math.min(passedTests / totalTests * 10, 10), // More passes = higher score
        fullMark: 10
      },
      {
        subject: 'Completion',
        score: totalTests / 6 * 10, // Assuming 6 tests is full completion
        fullMark: 10
      }
    ];

    return {
      hasData: true,
      formattedResults, // Add this for chart access
      chronologicalResults,
      bestScoresByTest,
      scoreRanges,
      flatLearningCurve,
      timeVsScoreData,
      radarData,
      learningCurveData,
      metrics: {
        totalTests,
        totalAttempts,
        passedTests,
        avgAttemptsPerTest,
        avgScore,
        totalTimeSpent,
        avgTimePerTest
      }
    };
  }, [testResults]);

  // Custom format for X-axis dates
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    return format(new Date(timestamp), 'MMM d');
  };

  // Handle empty data case AFTER the useMemo hook
  if (!processedData.hasData) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          No test results available for this student. Please select a student who has taken at least one test.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Top metrics overview cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Average Score</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{processedData.metrics.avgScore.toFixed(1)}%</div>
            <div className="text-xs text-muted-foreground mt-1">
              {processedData.metrics.passedTests} of {processedData.metrics.totalTests} tests passed
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Attempts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{processedData.metrics.totalAttempts}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {processedData.metrics.avgAttemptsPerTest.toFixed(1)} per test
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Time Spent</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatTimeSpent(processedData.metrics.totalTimeSpent * 60)}</div>
            <div className="text-xs text-muted-foreground mt-1">
              Avg: {Math.round(processedData.metrics.avgTimePerTest)} min/test
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Tests Completed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{processedData.metrics.totalTests}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {(processedData.metrics.totalTests / 6 * 100).toFixed(0)}% of curriculum
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Pass Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {(processedData.metrics.passedTests / processedData.metrics.totalTests * 100).toFixed(0)}%
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {processedData.metrics.passedTests}/{processedData.metrics.totalTests} tests passed
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main visualizations */}
      <Tabs defaultValue="performance" className="w-full">
        <TabsList>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="learning">Learning Progress</TabsTrigger>
          <TabsTrigger value="analysis">Detailed Analysis</TabsTrigger>
        </TabsList>

        <TabsContent value="performance" className="space-y-6 mt-6">
          {/* Score Timeline */}
          <Card>
            <CardHeader>
              <CardTitle>Score Timeline</CardTitle>
              <CardDescription>
                Test scores over time showing learning progression
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={processedData.chronologicalResults}>
                    <defs>
                      <linearGradient id="scoreGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS.chartGradientStart} stopOpacity={0.8}/>
                        <stop offset="95%" stopColor={COLORS.chartGradientEnd} stopOpacity={0.2}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="timestamp"
                      tickFormatter={formatDate}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis domain={[0, 100]} />
                    <Tooltip 
                      formatter={(value, name) => [
                        `${value.toFixed(1)}%`, 
                        name === 'score' ? 'Score' : name
                      ]}
                      labelFormatter={(label) => format(new Date(label), 'MMM d, yyyy')}
                    />
                    <ReferenceLine y={90} stroke={COLORS.success} strokeDasharray="3 3" label="Pass Threshold" />
                    <Area 
                      type="monotone" 
                      dataKey="score" 
                      stroke={COLORS.primary} 
                      fillOpacity={1} 
                      fill="url(#scoreGradient)" 
                      activeDot={{ r: 6 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Score Distribution & Best Scores */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Best Scores by Test */}
            <Card>
              <CardHeader>
                <CardTitle>Best Scores by Test</CardTitle>
                <CardDescription>Highest achieved score for each test</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[350px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart 
                      data={processedData.bestScoresByTest.sort((a, b) => b.bestScore - a.bestScore)} 
                      margin={{ top: 5, right: 30, left: 20, bottom: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="testName" 
                        tick={{ fontSize: 12, angle: -45, textAnchor: 'end' }}
                        height={60}
                        interval={0}
                      />
                      <YAxis domain={[0, 100]} />
                      <Tooltip formatter={(value) => `${value.toFixed(1)}%`} />
                      <ReferenceLine y={90} stroke={COLORS.success} strokeDasharray="3 3" />
                      <Bar dataKey="bestScore" name="Best Score">
                        {processedData.bestScoresByTest.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={processedData.formattedResults.some(r => r.testName === entry.testName && r.passed === true) ? COLORS.success : COLORS.danger} 
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Score Distribution */}
            <Card>
              <CardHeader>
                <CardTitle>Score Distribution</CardTitle>
                <CardDescription>Distribution of all test attempts by score range</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[350px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart 
                      data={processedData.scoreRanges}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="range" />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <ReferenceLine x="90-100" stroke={COLORS.success} strokeDasharray="3 3" />
                      <Bar dataKey="count" name="Attempts">
                        {processedData.scoreRanges.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={entry.range === '90-100' ? COLORS.success : COLORS.primary} 
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Performance Radar */}
          <Card>
            <CardHeader>
              <CardTitle>Performance Profile</CardTitle>
              <CardDescription>Overall performance across key metrics</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart outerRadius="80%" data={processedData.radarData}>
                    <PolarGrid gridType="polygon" />
                    <PolarAngleAxis dataKey="subject" />
                    <PolarRadiusAxis domain={[0, 10]} />
                    <Tooltip formatter={(value) => [`${value}/10`, 'Score']} />
                    <Radar 
                      name="Performance" 
                      dataKey="score" 
                      stroke={COLORS.primary} 
                      fill={COLORS.primary} 
                      fillOpacity={0.5} 
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="learning" className="space-y-6 mt-6">
          {/* Learning Curve */}
          <Card>
            <CardHeader>
              <CardTitle>Learning Curve</CardTitle>
              <CardDescription>Score progression by attempt for each test</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart 
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      type="number" 
                      dataKey="attempt" 
                      allowDuplicatedCategory={false}
                      domain={[1, 'dataMax']}
                      label={{ value: 'Attempt Number', position: 'insideBottomRight', offset: -10 }}
                    />
                    <YAxis domain={[0, 100]} />
                    <Tooltip 
                      formatter={(value, name) => [`${value.toFixed(1)}%`, name]}
                      labelFormatter={(value) => `Attempt ${value}`}
                    />
                    <Legend />
                    <ReferenceLine y={90} stroke={COLORS.success} strokeDasharray="3 3" label="Pass Threshold" />
                    
                    {processedData.learningCurveData.map((test, index) => (
                      <Line
                        key={test.testName}
                        data={test.progression}
                        type="monotone"
                        dataKey="score"
                        name={test.testName}
                        stroke={COLORS.pieColors[index % COLORS.pieColors.length]}
                        strokeWidth={2}
                        dot={{ r: 5 }}
                        activeDot={{ r: 8 }}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Time vs Score & Attempt Analysis */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Time vs. Score */}
            <Card>
              <CardHeader>
                <CardTitle>Time vs. Score Analysis</CardTitle>
                <CardDescription>Relationship between time spent and achieved scores</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[350px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <ScatterChart
                      margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                    >
                      <CartesianGrid />
                      <XAxis 
                        type="number" 
                        dataKey="timeSpent" 
                        name="Time Spent" 
                        unit=" min" 
                        label={{ value: 'Time Spent (minutes)', position: 'insideBottomRight', offset: -5 }} 
                      />
                      <YAxis 
                        type="number" 
                        dataKey="score" 
                        name="Score" 
                        unit="%" 
                        domain={[0, 100]} 
                      />
                      <Tooltip 
                        cursor={{ strokeDasharray: '3 3' }} 
                        formatter={(value, name) => [
                          name === 'Time Spent' ? `${value.toFixed(1)} min` : `${value.toFixed(1)}%`, 
                          name
                        ]}
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const data = payload[0].payload;
                            return (
                              <div className="bg-white p-2 border rounded shadow-sm">
                                <p className="font-medium">{data.testName}</p>
                                <p>Time: {data.timeSpent.toFixed(1)} min</p>
                                <p>Score: {data.score.toFixed(1)}%</p>
                                <p className={data.passed ? 'text-green-500' : 'text-red-500'}>
                                  {data.passed ? 'Passed' : 'Failed'}
                                </p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Scatter 
                        name="Time vs Score" 
                        data={processedData.timeVsScoreData} 
                        fill={COLORS.primary}
                      >
                        {processedData.timeVsScoreData.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={entry.passed ? COLORS.success : COLORS.danger} 
                          />
                        ))}
                      </Scatter>
                      <ReferenceLine y={90} stroke={COLORS.success} strokeDasharray="3 3" />
                    </ScatterChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Attempts by Test */}
            <Card>
              <CardHeader>
                <CardTitle>Attempts by Test</CardTitle>
                <CardDescription>Number of attempts needed for each test</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[350px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart 
                      data={processedData.bestScoresByTest}
                      margin={{ top: 5, right: 30, left: 20, bottom: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="testName" 
                        tick={{ fontSize: 12, angle: -45, textAnchor: 'end' }}
                        height={60}
                        interval={0}
                      />
                      <YAxis allowDecimals={false} />
                      <Tooltip formatter={(value) => value} />
                      <Bar dataKey="attempts" name="Attempts">
                        {processedData.bestScoresByTest.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={entry.attempts <= 2 ? COLORS.success : 
                                  entry.attempts <= 4 ? COLORS.warning : COLORS.danger} 
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="analysis" className="space-y-6 mt-6">
          {/* Average Test Time & Average Scores */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Average Completion Time */}
            <Card>
              <CardHeader>
                <CardTitle>Average Completion Time</CardTitle>
                <CardDescription>Average time spent on each test</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[350px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart 
                      data={processedData.bestScoresByTest}
                      margin={{ top: 5, right: 30, left: 20, bottom: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="testName" 
                        tick={{ fontSize: 12, angle: -45, textAnchor: 'end' }}
                        height={60}
                        interval={0}
                      />
                      <YAxis 
                        label={{ value: 'Minutes', angle: -90, position: 'insideLeft' }}
                      />
                      <Tooltip formatter={(value) => `${value.toFixed(1)} min`} />
                      <Bar dataKey="averageTime" name="Average Time" fill={COLORS.accent} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Average Score by Test */}
            <Card>
              <CardHeader>
                <CardTitle>Average Score by Test</CardTitle>
                <CardDescription>Average scores across all attempts</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[350px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart 
                      data={processedData.bestScoresByTest}
                      margin={{ top: 5, right: 30, left: 20, bottom: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="testName" 
                        tick={{ fontSize: 12, angle: -45, textAnchor: 'end' }}
                        height={60}
                        interval={0}
                      />
                      <YAxis domain={[0, 100]} />
                      <Tooltip formatter={(value) => `${value.toFixed(1)}%`} />
                      <ReferenceLine y={90} stroke={COLORS.success} strokeDasharray="3 3" />
                      <Bar dataKey="averageScore" name="Average Score">
                        {processedData.bestScoresByTest.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={
                              processedData.formattedResults.some(r => r.testName === entry.testName && r.passed === true) ? COLORS.success :
                              entry.averageScore >= 70 ? COLORS.warning : COLORS.danger
                            } 
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Passing Status Distribution */}
          <Card>
            <CardHeader>
              <CardTitle>Pass Status Distribution</CardTitle>
              <CardDescription>Proportion of passed vs failed tests</CardDescription>
            </CardHeader>
            <CardContent className="flex justify-center">
              <div className="h-[350px] w-full max-w-lg">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Passed', value: processedData.metrics.passedTests },
                        { name: 'Failed', value: processedData.metrics.totalTests - processedData.metrics.passedTests }
                      ]}
                      cx="50%"
                      cy="50%"
                      labelLine={true}
                      outerRadius={120}
                      fill="#8884d8"
                      dataKey="value"
                      nameKey="name"
                      label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      <Cell fill={COLORS.success} />
                      <Cell fill={COLORS.danger} />
                    </Pie>
                    <Tooltip formatter={(value) => value} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default StudentMetrics;